import React, { useState, useCallback, memo } from 'react';
import { X, Save, Plus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { whatsappTemplatesAPI, whatsappAPI } from '../../services/api';

const IsolatedForm = memo(({ 
  isOpen, 
  onClose, 
  editingTemplate, 
  categories, 
  onSuccess,
  aiGeneratedData 
}) => {
  const [templateName, setTemplateName] = useState(editingTemplate?.template_name || '');
  const [templateCategory, setTemplateCategory] = useState(editingTemplate?.template_category || '');
  const [templateLanguage, setTemplateLanguage] = useState(editingTemplate?.template_language || 'en');
  const [templateContent, setTemplateContent] = useState(editingTemplate?.template_content || '');
  const [templateVariables, setTemplateVariables] = useState(() => {
    if (editingTemplate?.template_variables) {
      try {
        // If it's a string, parse it as JSON
        if (typeof editingTemplate.template_variables === 'string') {
          return JSON.parse(editingTemplate.template_variables);
        }
        // If it's already an array, return it
        if (Array.isArray(editingTemplate.template_variables)) {
          return editingTemplate.template_variables;
        }
      } catch (error) {
        console.error('Error parsing template variables:', error);
      }
    }
    return [];
  });
  const [isActive, setIsActive] = useState(editingTemplate?.is_active || 1);
  const [loading, setLoading] = useState(false);

  // Header fields
  const [headerText, setHeaderText] = useState(editingTemplate?.header_text || '');
  const [headerType, setHeaderType] = useState(editingTemplate?.header_type || 'TEXT');
  const [headerMediaUrl, setHeaderMediaUrl] = useState(editingTemplate?.header_media_url || '');
  const [headerMediaFile, setHeaderMediaFile] = useState(null);
  const [headerUploadMethod, setHeaderUploadMethod] = useState('url');

  // Footer fields
  const [footerText, setFooterText] = useState(editingTemplate?.footer_text || '');

  // Button fields
  const [buttonType, setButtonType] = useState(editingTemplate?.button_type || '');
  const [buttonText, setButtonText] = useState(editingTemplate?.button_text || '');
  const [buttonUrl, setButtonUrl] = useState(editingTemplate?.button_url || '');
  const [buttonPhone, setButtonPhone] = useState(editingTemplate?.button_phone || '');

  // Media fields
  const [mediaType, setMediaType] = useState(editingTemplate?.media_type || '');
  const [mediaUrl, setMediaUrl] = useState(editingTemplate?.media_url || '');
  const [mediaCaption, setMediaCaption] = useState(editingTemplate?.media_caption || '');
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaUploadMethod, setMediaUploadMethod] = useState('url'); // 'url' or 'upload'
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [isUploadingHeaderMedia, setIsUploadingHeaderMedia] = useState(false);

  // Reset form when editing template changes
  React.useEffect(() => {
    if (editingTemplate) {
      setTemplateName(editingTemplate.template_name || '');
      setTemplateCategory(editingTemplate.template_category || '');
      setTemplateLanguage(editingTemplate.template_language || 'en');
      setTemplateContent(editingTemplate.template_content || '');
      // Handle template variables - could be string (JSON) or array
      let variables = [];
      if (editingTemplate.template_variables) {
        try {
          if (typeof editingTemplate.template_variables === 'string') {
            variables = JSON.parse(editingTemplate.template_variables);
          } else if (Array.isArray(editingTemplate.template_variables)) {
            variables = editingTemplate.template_variables;
          }
        } catch (error) {
          console.error('Error parsing template variables:', error);
          variables = [];
        }
      }
      setTemplateVariables(variables);
      setIsActive(editingTemplate.is_active || 1);
      
      // Reset new fields
      setHeaderText(editingTemplate.header_text || '');
      setHeaderType(editingTemplate.header_type || 'TEXT');
      setHeaderMediaUrl(editingTemplate.header_media_url || '');
      setFooterText(editingTemplate.footer_text || '');
      setButtonType(editingTemplate.button_type || '');
      setButtonText(editingTemplate.button_text || '');
      setButtonUrl(editingTemplate.button_url || '');
      setButtonPhone(editingTemplate.button_phone || '');
      setMediaType(editingTemplate.media_type || '');
      setMediaUrl(editingTemplate.media_url || '');
      setMediaCaption(editingTemplate.media_caption || '');
    } else {
      setTemplateName('');
      setTemplateCategory('');
      setTemplateLanguage('en');
      setTemplateContent('');
      setTemplateVariables([]);
      setIsActive(1);
      
      // Reset new fields
      setHeaderText('');
      setHeaderType('TEXT');
      setHeaderMediaUrl('');
      setFooterText('');
      setButtonType('');
      setButtonText('');
      setButtonUrl('');
      setButtonPhone('');
      setMediaType('');
      setMediaUrl('');
      setMediaCaption('');
    }
  }, [editingTemplate]);

  // Handle AI-generated data
  React.useEffect(() => {
    if (aiGeneratedData) {
      setTemplateName(aiGeneratedData.template_name || '');
      setTemplateCategory(aiGeneratedData.category || '');
      setTemplateLanguage(aiGeneratedData.language || 'en');
      setTemplateContent(aiGeneratedData.body_text || aiGeneratedData.template_content || '');
      // Handle AI-generated variables - ensure it's always an array
      let aiVariables = [];
      if (aiGeneratedData.variables) {
        if (Array.isArray(aiGeneratedData.variables)) {
          aiVariables = aiGeneratedData.variables;
        } else if (typeof aiGeneratedData.variables === 'string') {
          try {
            aiVariables = JSON.parse(aiGeneratedData.variables);
          } catch (error) {
            console.error('Error parsing AI variables:', error);
            aiVariables = [];
          }
        }
      }
      setTemplateVariables(aiVariables);
      setIsActive(1);
      
      // Set AI-generated header, footer, and other fields
      setHeaderText(aiGeneratedData.header_text || '');
      setHeaderType(aiGeneratedData.header_type || 'TEXT');
      setHeaderMediaUrl(aiGeneratedData.header_media_url || '');
      setFooterText(aiGeneratedData.footer_text || '');
      setButtonType(aiGeneratedData.button_type || '');
      setButtonText(aiGeneratedData.button_text || '');
      setButtonUrl(aiGeneratedData.button_url || '');
      setButtonPhone(aiGeneratedData.button_phone || '');
      setMediaType(aiGeneratedData.media_type || '');
      setMediaUrl(aiGeneratedData.media_url || '');
      setMediaCaption(aiGeneratedData.media_caption || '');
    }
  }, [aiGeneratedData]);

  const addVariable = useCallback(() => {
    const newVar = prompt('Enter variable name (e.g., {{name}}):');
    if (newVar && !templateVariables.includes(newVar)) {
      setTemplateVariables([...templateVariables, newVar]);
    }
  }, [templateVariables]);

  const removeVariable = useCallback((index) => {
    setTemplateVariables(templateVariables.filter((_, i) => i !== index));
  }, [templateVariables]);

  const handleMediaFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setMediaFile(file);
      setIsUploadingMedia(true);
      
      // Create a preview URL for immediate display
      const previewUrl = URL.createObjectURL(file);
      setMediaUrl(previewUrl);
      console.log('📱 Media preview URL created:', previewUrl);
      
      // Upload the file to get the real URL
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        console.log('📤 Uploading media file:', {
          name: file.name,
          size: file.size,
          type: file.type
        });
        
        const response = await whatsappAPI.uploadMedia(formData);
        
        if (response.data.success) {
          // Replace preview URL with real uploaded URL
          setMediaUrl(response.data.data.url);
          console.log('✅ Media file uploaded successfully:', {
            url: response.data.data.url,
            storageType: response.data.data.storageType,
            filename: response.data.data.filename
          });
          
          // Clean up the preview URL to free memory
          URL.revokeObjectURL(previewUrl);
        } else {
          console.error('❌ Upload response not successful:', response.data);
        }
      } catch (error) {
        console.error('❌ Error uploading media file:', error);
      } finally {
        setIsUploadingMedia(false);
      }
    }
  };

  const handleHeaderMediaFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setHeaderMediaFile(file);
      setIsUploadingHeaderMedia(true);
      
      // Create a preview URL for immediate display
      const previewUrl = URL.createObjectURL(file);
      setHeaderMediaUrl(previewUrl);
      console.log('📱 Header media preview URL created:', previewUrl);
      
      // Upload the file to get the real URL
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        console.log('📤 Uploading header media file:', {
          name: file.name,
          size: file.size,
          type: file.type
        });
        
        const response = await whatsappAPI.uploadMedia(formData);
        
        if (response.data.success) {
          // Replace preview URL with real uploaded URL
          setHeaderMediaUrl(response.data.data.url);
          console.log('✅ Header media file uploaded successfully:', {
            url: response.data.data.url,
            storageType: response.data.data.storageType,
            filename: response.data.data.filename
          });
          
          // Clean up the preview URL to free memory
          URL.revokeObjectURL(previewUrl);
        } else {
          console.error('❌ Header upload response not successful:', response.data);
        }
      } catch (error) {
        console.error('❌ Error uploading header media file:', error);
      } finally {
        setIsUploadingHeaderMedia(false);
      }
    }
  };

  // Template preview component
  const renderTemplatePreview = () => {
    return (
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border-2 border-dashed border-gray-300 dark:border-gray-600">
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
          📱 Template Preview
        </h4>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm max-w-sm mx-auto">
          {/* Header */}
          {headerText && headerType === 'TEXT' && (
            <div className="text-center mb-3">
              <div className="text-sm font-semibold text-gray-800 dark:text-gray-200 bg-blue-50 dark:bg-blue-900/20 px-3 py-2 rounded">
                {headerText}
              </div>
            </div>
          )}
          {headerType !== 'TEXT' && headerMediaUrl && (
            <div className="text-center mb-3">
              {headerType === 'IMAGE' ? (
                <img 
                  src={headerMediaUrl} 
                  alt="Header" 
                  className="max-w-full h-32 object-cover rounded-lg mx-auto"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
              ) : headerType === 'VIDEO' ? (
                <video 
                  src={headerMediaUrl} 
                  className="max-w-full h-32 object-cover rounded-lg mx-auto"
                  controls
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
              ) : (
                <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                  📎 {headerType} Header
                </div>
              )}
              <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded mt-2" style={{display: 'none'}}>
                📎 {headerType} Header (Failed to load)
              </div>
            </div>
          )}
          
          {/* Body */}
          <div className="text-sm text-gray-800 dark:text-gray-200 mb-3">
            {templateContent || 'Template content will appear here...'}
          </div>
          
          {/* Footer */}
          {footerText && (
            <div className="text-xs text-gray-500 dark:text-gray-400 text-center border-t pt-2">
              {footerText}
            </div>
          )}
          
          {/* Buttons */}
          {buttonType && buttonText && (
            <div className="mt-3 pt-3 border-t">
              {buttonType === 'QUICK_REPLY' ? (
                <div className="flex flex-wrap gap-1">
                  <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded">
                    {buttonText}
                  </span>
                </div>
              ) : (
                <div className="text-center">
                  <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1 rounded">
                    {buttonText}
                  </span>
                </div>
              )}
            </div>
          )}
          
          {/* Media */}
          {mediaType && mediaUrl && (
            <div className="mt-3 pt-3 border-t">
              {mediaType === 'IMAGE' ? (
                <div className="text-center">
                  <img 
                    src={mediaUrl} 
                    alt="Media" 
                    className="max-w-full h-32 object-cover rounded-lg mx-auto"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded mt-2" style={{display: 'none'}}>
                    📎 Image (Failed to load)
                  </div>
                  {mediaCaption && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{mediaCaption}</div>}
                </div>
              ) : mediaType === 'VIDEO' ? (
                <div className="text-center">
                  <video 
                    src={mediaUrl} 
                    className="max-w-full h-32 object-cover rounded-lg mx-auto"
                    controls
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded mt-2" style={{display: 'none'}}>
                    📎 Video (Failed to load)
                  </div>
                  {mediaCaption && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{mediaCaption}</div>}
                </div>
              ) : mediaType === 'AUDIO' ? (
                <div className="text-center">
                  <audio 
                    src={mediaUrl} 
                    controls
                    className="w-full"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded mt-2" style={{display: 'none'}}>
                    📎 Audio (Failed to load)
                  </div>
                  {mediaCaption && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{mediaCaption}</div>}
                </div>
              ) : (
                <div className="text-center">
                  <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                    📎 {mediaType} Attachment
                  </div>
                  {mediaCaption && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{mediaCaption}</div>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = {
        template_name: templateName,
        template_category: templateCategory,
        template_language: templateLanguage,
        template_content: templateContent,
        template_variables: templateVariables,
        is_active: isActive,
        // Header fields
        header_text: headerText,
        header_type: headerType,
        header_media_url: headerMediaUrl,
        // Footer fields
        footer_text: footerText,
        // Button fields
        button_type: buttonType,
        button_text: buttonText,
        button_url: buttonUrl,
        button_phone: buttonPhone,
        // Media fields
        media_type: mediaType,
        media_url: mediaUrl,
        media_caption: mediaCaption
      };

      if (editingTemplate) {
        await whatsappTemplatesAPI.updateTemplate(editingTemplate.id, formData);
        toast.success('Template updated successfully!');
      } else {
        await whatsappTemplatesAPI.createTemplate(formData);
        toast.success('Template created successfully!');
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(error.response?.data?.message || 'Failed to save template');
    } finally {
      setLoading(false);
    }
  }, [templateName, templateCategory, templateLanguage, templateContent, templateVariables, isActive, editingTemplate, onSuccess, onClose]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">
            {editingTemplate ? 'Edit Template' : 'Add New Template'}
          </h2>
          <button
            onClick={handleClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form Content */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder-gray-500 dark:placeholder-gray-400 shadow-sm dark:shadow-md"
                  placeholder="Enter template name"
                  required
                  autoComplete="off"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Category *
                </label>
                <select
                  value={templateCategory}
                  onChange={(e) => setTemplateCategory(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm dark:shadow-md"
                  required
                >
                  <option value="">Select category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Language
                </label>
                <select
                  value={templateLanguage}
                  onChange={(e) => setTemplateLanguage(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm dark:shadow-md"
                >
                  <option value="en">English</option>
                  <option value="hi">Hindi</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Status
                </label>
                <select
                  value={isActive}
                  onChange={(e) => setIsActive(parseInt(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm dark:shadow-md"
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
            </div>

            {/* Template Content */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Template Content *
              </label>
              <textarea
                value={templateContent}
                onChange={(e) => setTemplateContent(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder-gray-500 dark:placeholder-gray-400 resize-none shadow-sm dark:shadow-md"
                rows={6}
                placeholder="Enter your message template content. Use {{variableName}} for dynamic content."
                required
                autoComplete="off"
              />
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Use double curly braces for variables: {`{{name}}, {{orderNumber}}, etc.`}
              </p>
            </div>

            {/* Variables */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Template Variables
                </label>
                <button
                  type="button"
                  onClick={addVariable}
                  className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors text-sm font-medium"
                >
                  <Plus className="w-4 h-4 inline mr-1" />
                  Add Variable
                </button>
              </div>
              <div className="space-y-2">
                {Array.isArray(templateVariables) && templateVariables.map((variable, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <span className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-mono">
                      {variable}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeVariable(index)}
                      className="p-1 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {Array.isArray(templateVariables) && templateVariables.length === 0 && (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">No variables added yet</p>
                )}
              </div>
            </div>

            {/* Template Preview */}
            {renderTemplatePreview()}

            {/* Header Section */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center">
                📋 Header Options
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Header Type
                  </label>
                  <select
                    value={headerType}
                    onChange={(e) => setHeaderType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="TEXT">Text Header</option>
                    <option value="IMAGE">Image Header</option>
                    <option value="VIDEO">Video Header</option>
                    <option value="DOCUMENT">Document Header</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {headerType === 'TEXT' ? 'Header Text' : 'Header Media'}
                  </label>
                  {headerType === 'TEXT' ? (
                    <input
                      type="text"
                      value={headerText}
                      onChange={(e) => setHeaderText(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter header text"
                    />
                  ) : (
                    <div className="space-y-3">
                      <div className="flex space-x-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="url"
                            checked={headerUploadMethod === 'url'}
                            onChange={(e) => setHeaderUploadMethod(e.target.value)}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">URL</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="upload"
                            checked={headerUploadMethod === 'upload'}
                            onChange={(e) => setHeaderUploadMethod(e.target.value)}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Upload File</span>
                        </label>
                      </div>
                      
                      {headerUploadMethod === 'url' ? (
                        <input
                          type="url"
                          value={headerMediaUrl}
                          onChange={(e) => setHeaderMediaUrl(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter media URL"
                        />
                      ) : (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Upload Header Media
                            {isUploadingHeaderMedia && (
                              <span className="ml-2 text-blue-600 dark:text-blue-400 text-xs">
                                📤 Uploading...
                              </span>
                            )}
                          </label>
                          <input
                            type="file"
                            onChange={handleHeaderMediaFileChange}
                            disabled={isUploadingHeaderMedia}
                            accept={
                              headerType === 'IMAGE' ? 'image/*' :
                              headerType === 'VIDEO' ? 'video/*' :
                              '*/*'
                            }
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          {headerMediaFile && (
                            <div className="mt-2">
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                Selected: {headerMediaFile.name} ({(headerMediaFile.size / 1024 / 1024).toFixed(2)} MB)
                              </p>
                              {isUploadingHeaderMedia && (
                                <div className="flex items-center mt-1">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                  <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">Uploading to {headerMediaUrl.startsWith('http') && headerMediaUrl.includes('amazonaws') ? 'S3' : 'server'}...</span>
                                </div>
                              )}
                              {!isUploadingHeaderMedia && headerMediaUrl && headerMediaUrl.startsWith('http') && (
                                <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                                  ✅ Uploaded successfully to {headerMediaUrl.includes('amazonaws') ? 'S3' : 'server'}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer Section */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center">
                📝 Footer Options
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Footer Text
                </label>
                <input
                  type="text"
                  value={footerText}
                  onChange={(e) => setFooterText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter footer text"
                />
              </div>
            </div>

            {/* Button Section */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center">
                🔘 Button Options
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Button Type
                  </label>
                  <select
                    value={buttonType}
                    onChange={(e) => setButtonType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">No Button</option>
                    <option value="QUICK_REPLY">Quick Reply</option>
                    <option value="CALL_TO_ACTION">Call to Action</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Button Text
                  </label>
                  <input
                    type="text"
                    value={buttonText}
                    onChange={(e) => setButtonText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter button text"
                    disabled={!buttonType}
                  />
                </div>
                {buttonType === 'CALL_TO_ACTION' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Button URL
                      </label>
                      <input
                        type="url"
                        value={buttonUrl}
                        onChange={(e) => setButtonUrl(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter URL for call-to-action"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={buttonPhone}
                        onChange={(e) => setButtonPhone(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter phone number for call button"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Media Section */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center">
                📎 Media Attachment
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Media Type
                  </label>
                  <select
                    value={mediaType}
                    onChange={(e) => setMediaType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">No Media</option>
                    <option value="IMAGE">Image</option>
                    <option value="VIDEO">Video</option>
                    <option value="DOCUMENT">Document</option>
                    <option value="AUDIO">Audio</option>
                  </select>
                </div>
                
                {mediaType && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Upload Method
                      </label>
                      <div className="flex space-x-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="url"
                            checked={mediaUploadMethod === 'url'}
                            onChange={(e) => setMediaUploadMethod(e.target.value)}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">URL</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="upload"
                            checked={mediaUploadMethod === 'upload'}
                            onChange={(e) => setMediaUploadMethod(e.target.value)}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Upload File</span>
                        </label>
                      </div>
                    </div>
                    
                    {mediaUploadMethod === 'url' ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Media URL
                        </label>
                        <input
                          type="url"
                          value={mediaUrl}
                          onChange={(e) => setMediaUrl(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter media URL"
                        />
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Upload Media File
                          {isUploadingMedia && (
                            <span className="ml-2 text-blue-600 dark:text-blue-400 text-xs">
                              📤 Uploading...
                            </span>
                          )}
                        </label>
                        <input
                          type="file"
                          onChange={handleMediaFileChange}
                          disabled={isUploadingMedia}
                          accept={
                            mediaType === 'IMAGE' ? 'image/*' :
                            mediaType === 'VIDEO' ? 'video/*' :
                            mediaType === 'AUDIO' ? 'audio/*' :
                            '*/*'
                          }
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        {mediaFile && (
                          <div className="mt-2">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Selected: {mediaFile.name} ({(mediaFile.size / 1024 / 1024).toFixed(2)} MB)
                            </p>
                            {isUploadingMedia && (
                              <div className="flex items-center mt-1">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">Uploading to {mediaUrl.startsWith('http') && mediaUrl.includes('amazonaws') ? 'S3' : 'server'}...</span>
                              </div>
                            )}
                            {!isUploadingMedia && mediaUrl && mediaUrl.startsWith('http') && (
                              <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                                ✅ Uploaded successfully to {mediaUrl.includes('amazonaws') ? 'S3' : 'server'}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Media Caption
                      </label>
                      <textarea
                        value={mediaCaption}
                        onChange={(e) => setMediaCaption(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        rows={2}
                        placeholder="Enter media caption"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleClose}
                className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>{loading ? 'Saving...' : (editingTemplate ? 'Update Template' : 'Create Template')}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
});

IsolatedForm.displayName = 'IsolatedForm';

export default IsolatedForm;
